import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ClipboardList,
  History,
  Settings,
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  CircleDot,
  Mail,
  Briefcase,
  ExternalLink,
  Check,
  Send,
  MessageSquare,
  SkipForward,
  Play,
  Pause,
  RotateCcw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Loader2 as SpinIcon,
  Wand2,
  Pencil,
  Save,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { requisitionService } from '@core/supabase/services/requisitionService';
import { requisitionHistoryService, type RequisitionHistoryEntry } from '@core/supabase/services/requisitionHistoryService';
import { requisitionClientGateService, resolveHiringClientEmail } from '@core/supabase/services/requisitionClientGateService';
import type { Requisition } from '@core/types';
import type { RequisitionStatus } from '@core/enums';
import {
  REQUISITION_STATUS_LABELS,
  REQUISITION_FLOW_STEPS,
  REQUISITION_LIFECYCLE_PHASES,
  getRequisitionFlowPhaseIndex,
} from '@core/constants/requisitionLifecycle';
import { getRequisitionContextActions } from '@core/constants/requisitionContextActions';
import { invokeLlamaChat } from '@core/ai/llamaChat';
import { RecordDetailLayout } from '@shared/components/ui/RecordDetailLayout';
import { RequisitionHeaderBand } from './RequisitionHeaderBand';
import { RequisitionDashboardStack } from './RequisitionDashboardStack';
import {
  ProgressChip,
  AvatarStack,
  type AvatarStackPerson,
} from '@shared/components/viz';
import { useTrackRecentlyViewed } from '@shared/hooks/useRecentlyViewed';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { detailTbBtn, detailTbNeutral, detailTbDanger } from '@shared/lib/detailToolbarStyles';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { Modal } from '@shared/components/ui/Modal';
import { CandidateCollapsiblePanel } from '../candidates/CandidateCollapsiblePanel';
import { RequisitionForm } from './RequisitionForm';
import { RequisitionAiPanel } from './RequisitionAiPanel';
import { RequisitionTemplatesPanel } from './RequisitionTemplatesPanel';
import { RequisitionIntakeWizardPage } from './RequisitionIntakeWizardPage';
import { JobNewWizardModal } from '../jobs/JobNewWizardModal';
import type { RequisitionFormAssistPatch } from './requisitionAssistTypes';
import { emptyRequisitionAiContext } from './requisitionAssistTypes';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { PositionTimelineDrawer } from '../reports/PositionTimelineDrawer';

const BRAND = '#8B80F9';
const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  low:    { label: 'Low',    color: '#6b7280', bg: '#6b728015', border: '#6b728040' },
  normal: { label: 'Normal', color: '#10b981', bg: '#10b98115', border: '#10b98140' },
  high:   { label: 'High',   color: '#f59e0b', bg: '#f59e0b15', border: '#f59e0b40' },
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#ef444415', border: '#ef444440' },
};

/** Shared dt/dd — colors + wrap; row chrome comes from global `.ggj-attr-grid` rules */
const PARAM_DT = 'vscode-text-secondary pr-2 min-w-0';
const PARAM_DD = 'vscode-text-primary min-w-0 break-words';

function refLabel(id: string) {
  return `REQ-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;
}

function recordInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}
function fmtDate(iso: string | null | undefined) {
  return formatDateTimeGmt(iso);
}
function fmtDateLong(iso: string | null | undefined) {
  return formatDateTimeGmt(iso);
}

const STATUS_ICON: Record<string, React.ReactNode> = {
  draft:           <CircleDot    className="w-4 h-4 vscode-text-tertiary" />,
  awaiting_client: <Mail         className="w-4 h-4 text-sky-400" />,
  submitted:       <Clock        className="w-4 h-4 text-amber-400" />,
  approved:        <CheckCircle2 className="w-4 h-4 text-sky-400" />,
  rejected:        <XCircle      className="w-4 h-4 text-red-400" />,
  sourcing:        <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
  filled:          <CheckCircle2 className="w-4 h-4 text-teal-400" />,
  on_hold:         <Clock        className="w-4 h-4 text-yellow-400" />,
  cancelled:       <Ban          className="w-4 h-4 vscode-text-tertiary" />,
};

type ValidTab = 'overview' | 'details' | 'sourcing' | 'history' | 'parameters';
const VALID_TABS: ValidTab[] = ['overview', 'details', 'sourcing', 'history', 'parameters'];

/** Tabs — no icons, matching JobDetailPage pattern */
const TABS: { id: ValidTab; label: string }[] = [
  { id: 'overview',   label: 'Overview'    },
  { id: 'details',    label: 'Details'     },
  { id: 'sourcing',   label: 'Sourcing'    },
  { id: 'history',    label: 'History'     },
  { id: 'parameters', label: 'Parameters'  },
];

/* ─── Requisition phase progress chart ─── */
function RequisitionPhaseChart({ req }: { req: Requisition }) {
  const phaseIdx  = getRequisitionFlowPhaseIndex(req.status);
  const isOffPath = req.status === 'rejected' || req.status === 'cancelled';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">
          Lifecycle progress
        </p>
        {isOffPath && (
          <span className="text-xs px-2 py-0.5 rounded-md font-medium"
            style={{ backgroundColor: '#ef444420', color: '#f87171', border: '1px solid #ef444440' }}>
            Off path
          </span>
        )}
      </div>

      {/* Horizontal phase track */}
      <div className="relative mb-4">
        <div className="flex items-center gap-0 w-full">
          {REQUISITION_LIFECYCLE_PHASES.map((phase, idx) => {
            const done   = idx < phaseIdx && !isOffPath;
            const active = idx === phaseIdx && !isOffPath;
            const isLast = idx === REQUISITION_LIFECYCLE_PHASES.length - 1;
            return (
              <div key={phase.key} className="flex items-center min-w-0"
                style={{ flex: isLast ? '0 0 auto' : '1 1 0' }}>
                <div className="relative flex flex-col items-center z-10">
                  <div className={[
                    'w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all',
                    done   ? 'border-emerald-500 bg-emerald-500' : '',
                    active ? 'border-2 scale-110' : '',
                    !done && !active ? 'border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)]' : '',
                  ].join(' ')}
                    style={active ? { borderColor: BRAND, backgroundColor: `${BRAND}25` } : {}}>
                    {done
                      ? <Check className="w-2.5 h-2.5 text-white" />
                      : active
                        ? <div className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND }} />
                        : <div className="w-1.5 h-1.5 rounded-full bg-[var(--vscode-border-primary)]" />
                    }
                  </div>
                </div>
                {!isLast && (
                  <div className="h-0.5 flex-1 mx-0.5"
                    style={{ backgroundColor: done ? '#10b981' : 'var(--vscode-border-primary)' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Phase labels */}
      <div className="space-y-1.5">
        {REQUISITION_LIFECYCLE_PHASES.map((phase, idx) => {
          const done   = idx < phaseIdx && !isOffPath;
          const active = idx === phaseIdx && !isOffPath;
          return (
            <div key={phase.key} className="flex items-center gap-2">
              <div className={[
                'w-1.5 h-1.5 rounded-full flex-shrink-0',
                done   ? 'bg-emerald-500' : '',
                !done && !active ? 'bg-[var(--vscode-border-primary)]' : '',
              ].join(' ')}
                style={active ? { backgroundColor: BRAND } : {}} />
              <span className={`text-xs ${active ? 'font-semibold' : done ? 'vscode-text-secondary' : 'vscode-text-tertiary'}`}
                style={active ? { color: BRAND } : {}}>
                {phase.short}
              </span>
              {active && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-medium"
                  style={{ backgroundColor: `${BRAND}20`, color: BRAND }}>
                  current
                </span>
              )}
              {done && <Check className="ml-auto w-3 h-3 text-emerald-500" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RequisitionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [req, setReq]         = useState<Requisition | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ValidTab>('overview');

  useTrackRecentlyViewed(
    useMemo(
      () =>
        req
          ? {
              kind: 'requisition' as const,
              id: req.id,
              label: (req as { title?: string | null; role_title?: string | null }).title
                || (req as { title?: string | null; role_title?: string | null }).role_title
                || 'Requisition',
              sublabel: (req as { status?: string }).status
                ? `Status: ${(req as { status?: string }).status}`
                : undefined,
              to: `/requisitions/${req.id}`,
            }
          : null,
      [req?.id, (req as { title?: string | null } | null)?.title, (req as { role_title?: string | null } | null)?.role_title, (req as { status?: string } | null)?.status],
    ),
  );

  /* per-tab edit modes */
  const [ovEditing, setOvEditing]   = useState(false);
  const [dtEditing, setDtEditing]   = useState(false);

  const [pendingAssist, setPendingAssist] = useState<RequisitionFormAssistPatch | null>(null);
  const [aiCtx, setAiCtx] = useState(emptyRequisitionAiContext());
  const [history, setHistory] = useState<RequisitionHistoryEntry[]>([]);
  const [approverNote, setApproverNote] = useState('');
  const [savingApproval, setSavingApproval] = useState(false);

  /* ── Action panel state ── */
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  type ComposeMode = 'reply' | 'confirm' | 'skip' | null;
  const [composeMode, setComposeMode] = useState<ComposeMode>(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody]     = useState('');
  const [composeToEmail, setComposeToEmail] = useState('');
  const [showActionHistory, setShowActionHistory] = useState(false);
  /* Email modal */
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailIsResend, setEmailIsResend]   = useState(false);
  const [emailAiLoading, setEmailAiLoading] = useState(false);
  const [emailAiOutput, setEmailAiOutput]   = useState('');
  const [intakeOpen, setIntakeOpen]         = useState(false);
  const [jobWizardOpen, setJobWizardOpen]   = useState(false);
  const [timelineOpen, setTimelineOpen]     = useState(false);

  useEffect(() => {
    const t = searchParams.get('tab') as ValidTab | null;
    if (t && VALID_TABS.includes(t)) setActiveTab(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    requisitionService.getById(id).then(setReq).finally(() => setLoading(false));
    requisitionHistoryService.listByRequisition(id).then(setHistory);
  }, [id]);

  useEffect(() => {
    if (!req) return;
    setAiCtx({
      title: req.title,
      department: req.department ?? '',
      clientName: req.client?.name,
      employment_type: req.employment_type ?? '',
      location: req.location ?? '',
      business_justification: req.business_justification ?? '',
      must_have_skills: [...(req.must_have_skills ?? [])],
      nice_to_have_skills: [...(req.nice_to_have_skills ?? [])],
    });
  }, [req]);

  const clearAssist = useCallback(() => setPendingAssist(null), []);

  const applyAssist = useCallback((patch: RequisitionFormAssistPatch) => {
    setOvEditing(true);
    setPendingAssist(patch);
    setActiveTab('overview');
  }, []);

  const handleApprovalAction = async (action: 'approved' | 'rejected') => {
    if (!req || !user?.orgId) return;
    setSavingApproval(true);
    try {
      const now = new Date().toISOString();
      const patch = action === 'approved'
        ? { status: 'approved' as const, approved_at: now }
        : { status: 'rejected' as const, rejected_at: now };
      await requisitionService.update(req.id, patch);
      await requisitionHistoryService.record(
        user.orgId, req.id, req.status, action,
        { changedBy: user.name, note: approverNote || undefined },
      );
      const entry: RequisitionHistoryEntry = {
        id: crypto.randomUUID(), org_id: user.orgId, requisition_id: req.id,
        from_status: req.status, to_status: action,
        changed_by: user.name ?? null, changed_by_id: null,
        note: approverNote || null, changed_at: now,
      };
      setHistory((prev) => [entry, ...prev]);
      setReq((prev) => prev ? { ...prev, ...patch } : prev);
      setApproverNote('');
    } finally { setSavingApproval(false); }
  };

  const handleApproveAndCreateJob = async () => {
    if (!req || !user?.orgUserId) return;
    setSavingApproval(true);
    try {
      await requisitionService.approveAndCreateJob(req.id, user.orgUserId, approverNote.trim() || undefined);
      const fresh = await requisitionService.getById(req.id);
      if (fresh) setReq(fresh);
      setHistory(await requisitionHistoryService.listByRequisition(req.id));
      setApproverNote('');
    } finally { setSavingApproval(false); }
  };

  const refreshAfterSave = async (updated: Requisition) => {
    setReq(updated);
    setHistory(await requisitionHistoryService.listByRequisition(updated.id));
  };

  /* ── Action helpers ── */
  const runAction = async (fn: () => Promise<Requisition | void>) => {
    setActionSaving(true);
    setActionError('');
    try {
      const result = await fn();
      if (result && typeof result === 'object' && 'id' in result) {
        await refreshAfterSave(result as Requisition);
      } else if (req) {
        const fresh = await requisitionService.getById(req.id);
        if (fresh) await refreshAfterSave(fresh);
      }
      setComposeMode(null);
      setComposeBody('');
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed. Please try again.');
    } finally {
      setActionSaving(false);
    }
  };

  const openEmailCompose = (resend = false) => {
    if (!req) return;
    const toEmail = resolveHiringClientEmail(req);
    setComposeToEmail(toEmail);
    setComposeSubject(`Hiring requisition: ${req.title}${req.client?.name ? ` — ${req.client.name}` : ''}`);
    setComposeBody(
      `Dear ${req.hiring_manager_name ?? 'Hiring Manager'},\n\n` +
      `We are writing to confirm receipt of the hiring requisition for the role of **${req.title}**.\n\n` +
      `Please review the attached details and confirm the mandate so we can proceed with sourcing the right candidates.\n\n` +
      `If you have any changes or additional requirements, please reply to this email.\n\n` +
      `Best regards,\n[Your name]`
    );
    setEmailIsResend(resend);
    setEmailModalOpen(true);
  };

  const handleSendEmail = async (resend = false) => {
    if (!req || !user?.orgId) return;
    if (!composeToEmail.trim()) { setActionError('Recipient email is required.'); return; }
    await runAction(() =>
      requisitionClientGateService.sendOutreachEmail({
        req, orgId: user.orgId!, toAddress: composeToEmail.trim(),
        subject: composeSubject.trim(), bodyPlain: composeBody.trim(),
        actorName: user.name ?? 'Recruiter', actorOrgUserId: user.orgUserId ?? null,
        resend,
      })
    );
    setEmailModalOpen(false);
  };

  const handleLogReply = async () => {
    if (!req || !user?.orgId || !composeBody.trim()) return;
    await runAction(async () => {
      await requisitionClientGateService.logClientReply({
        orgId: user.orgId!, requisitionId: req.id,
        body: composeBody.trim(), actorName: user.name ?? 'Recruiter',
      });
    });
  };

  const handleRecordConfirmation = async () => {
    if (!req || !user?.orgId) return;
    await runAction(() =>
      requisitionClientGateService.recordClientConfirmation({
        req, orgId: user.orgId!, summaryPlain: composeBody.trim() || 'Client confirmed via email.',
        actorName: user.name ?? 'Recruiter', actorOrgUserId: user.orgUserId ?? null,
      })
    );
  };

  const handleSkipGate = async () => {
    if (!req || !user?.orgId) return;
    await runAction(() =>
      requisitionClientGateService.skipClientGate({
        req, orgId: user.orgId!, actorName: user.name ?? 'Recruiter',
        actorOrgUserId: user.orgUserId ?? null, reason: composeBody.trim() || undefined,
      })
    );
  };

  const handleSimpleTransition = async (to: RequisitionStatus, note?: string) => {
    if (!req || !user?.orgId) return;
    await runAction(async () => {
      const now = new Date().toISOString();
      const patch: Partial<Requisition> = { status: to };
      if (to === 'approved')  patch.approved_at = now;
      if (to === 'rejected')  patch.rejected_at = now;
      const updated = await requisitionService.update(req.id, patch);
      await requisitionHistoryService.record(
        user.orgId!, req.id, req.status, to,
        { changedBy: user.name, note },
      );
      return updated;
    });
  };

  if (!user?.orgId) return null;
  if (loading) return <FullPageSpinner />;
  if (!req)    return <p className="vscode-text-secondary">Requisition not found.</p>;

  const linkedJobId  = req.job_id ?? req.created_job_id ?? null;
  const canCreateJob = ['approved', 'sourcing'].includes(req.status) && !linkedJobId;
  const phaseIdx     = getRequisitionFlowPhaseIndex(req.status);
  const currentPhase = phaseIdx >= 0 && phaseIdx < REQUISITION_FLOW_STEPS.length
    ? REQUISITION_FLOW_STEPS[phaseIdx] : null;
  const contextActions = getRequisitionContextActions(req);

  /* ─── shared sidebar sub-panels ─── */
  const statusSidebar = (prefix: string) => (
    <CandidateCollapsiblePanel storageKey={`req-${prefix}-status`} title="Status & lifecycle" defaultOpen>
      <div className="pt-3 space-y-3">
        <div className="rounded-lg border border-[var(--vscode-border-primary)] p-3"
          style={{ backgroundColor: `${BRAND}10` }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: BRAND }}>
            Current status
          </p>
          <div className="flex items-center gap-2">
            {STATUS_ICON[req.status]}
            <span className="text-sm font-medium vscode-text-primary">
              {REQUISITION_STATUS_LABELS[req.status] ?? req.status}
            </span>
          </div>
          {req.intake_wizard_step != null && (
            <p className="mt-2 text-xs text-amber-300 bg-amber-950/40 border border-amber-800/40 rounded px-2 py-1.5">
              Intake: step {req.intake_wizard_step}/5 — resume guided setup when ready.
            </p>
          )}
        </div>
        <div className="flex justify-between">
          <span className="text-xs vscode-text-tertiary">Reference</span>
          <span className="text-xs font-mono vscode-text-primary">{refLabel(req.id)}</span>
        </div>
        {currentPhase && (
          <div>
            <p className="text-xs vscode-text-tertiary mb-1">Journey phase</p>
            <p className="text-xs vscode-text-primary font-medium">{currentPhase.title}</p>
            <p className="text-xs vscode-text-tertiary mt-0.5 leading-relaxed">{currentPhase.description}</p>
          </div>
        )}
      </div>
    </CandidateCollapsiblePanel>
  );

  /* ── Compact action button helpers ── */
  const ABtn = ({ onClick, variant = 'primary', disabled = false, icon, children }: {
    onClick: () => void; variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
    disabled?: boolean; icon?: React.ReactNode; children: React.ReactNode;
  }) => {
    const styles: Record<string, React.CSSProperties> = {
      primary:   { background: BRAND, color: '#fff', border: `1px solid ${BRAND}` },
      secondary: { background: `${BRAND}12`, color: BRAND, border: `1px solid ${BRAND}40` },
      danger:    { background: '#ef444418', color: '#f87171', border: '1px solid #ef444445' },
      ghost:     { background: 'var(--vscode-bg-hover)', color: 'var(--vscode-text-secondary)', border: '1px solid var(--vscode-border-primary)' },
    };
    return (
      <button type="button" disabled={disabled || actionSaving}
        onClick={onClick}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-40"
        style={styles[variant]}>
        {icon && <span className="shrink-0 flex items-center">{icon}</span>}
        <span className="flex-1 text-left">{children}</span>
      </button>
    );
  };

  const ComposeArea = ({ placeholder, label, onSend, sendLabel }: {
    placeholder: string; label?: string; onSend: () => void; sendLabel: string;
  }) => (
    <div className="space-y-2 mt-2">
      {label && <p className="text-xs vscode-text-tertiary">{label}</p>}
      <textarea
        className="input w-full resize-none text-sm min-h-[72px]"
        placeholder={placeholder}
        value={composeBody}
        onChange={(e) => setComposeBody(e.target.value)}
        rows={3}
      />
      <div className="flex gap-2">
        <button type="button" onClick={onSend} disabled={actionSaving}
          className="flex-1 btn-primary text-sm inline-flex items-center justify-center gap-1.5"
          style={{ background: BRAND }}>
          {actionSaving ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {sendLabel}
        </button>
        <button type="button" onClick={() => { setComposeMode(null); setComposeBody(''); }}
          className="btn-secondary text-sm px-3">
          Cancel
        </button>
      </div>
    </div>
  );

  const actionsSidebar = (prefix: string) => {
    const s = req.status;
    const toEmail = resolveHiringClientEmail(req);
    const linked  = Boolean(req.job_id ?? req.created_job_id);

    return (
      <CandidateCollapsiblePanel storageKey={`req-${prefix}-actions`} title="Actions" defaultOpen>
        <div className="pt-3 space-y-2">
          {actionError && (
            <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/30 rounded-lg px-2.5 py-2">
              {actionError}
            </p>
          )}

          {/* ── DRAFT: incomplete intake ── */}
          {s === 'draft' && req.intake_wizard_step != null && (
            <button type="button"
              onClick={() => setIntakeOpen(true)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
              style={{ background: BRAND, color: '#fff', border: `1px solid ${BRAND}` }}>
              <ClipboardList className="w-3.5 h-3.5 shrink-0" />
              Finish intake (step {req.intake_wizard_step}/5)
            </button>
          )}

          {/* ── DRAFT: intake complete — client gate ── */}
          {s === 'draft' && req.intake_wizard_step == null && (
            <>
              {composeMode !== 'skip' && (
                <>
                  <ABtn variant="primary" icon={<Mail className="w-3.5 h-3.5" />}
                    onClick={() => openEmailCompose(false)}>
                    Email hiring client
                  </ABtn>
                  {!toEmail && (
                    <p className="text-xs text-amber-400/80">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      No client email — add hiring manager email first.
                    </p>
                  )}
                  <ABtn variant="ghost" icon={<SkipForward className="w-3.5 h-3.5" />}
                    onClick={() => { setComposeMode('skip'); setComposeBody(''); }}>
                    Skip — client already confirmed
                  </ABtn>
                </>
              )}
              {composeMode === 'skip' && (
                <ComposeArea
                  label="Reason for skipping client email (optional)"
                  placeholder="Client confirmed via phone call on…"
                  onSend={() => void handleSkipGate()}
                  sendLabel="Skip & submit for approval"
                />
              )}
            </>
          )}

          {/* ── AWAITING CLIENT ── */}
          {s === 'awaiting_client' && (
            <>
              {!composeMode && (
                <>
                  <ABtn variant="primary" icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                    onClick={() => { setComposeMode('confirm'); setComposeBody(''); }}>
                    Record client confirmation
                  </ABtn>
                  <ABtn variant="secondary" icon={<MessageSquare className="w-3.5 h-3.5" />}
                    onClick={() => { setComposeMode('reply'); setComposeBody(''); }}>
                    Log client reply
                  </ABtn>
                  <ABtn variant="secondary" icon={<Mail className="w-3.5 h-3.5" />}
                    onClick={() => openEmailCompose(true)}>
                    Resend outreach email
                  </ABtn>
                  <ABtn variant="ghost" icon={<RotateCcw className="w-3.5 h-3.5" />}
                    onClick={() => void handleSimpleTransition('draft', 'Moved back to draft')}>
                    Back to draft
                  </ABtn>
                </>
              )}
              {composeMode === 'confirm' && (
                <ComposeArea
                  label="Paste or summarise the client's confirmation"
                  placeholder="e.g. Client confirmed on 28 Mar via email: approved headcount of 2…"
                  onSend={() => void handleRecordConfirmation()}
                  sendLabel="Record confirmation & submit"
                />
              )}
              {composeMode === 'reply' && (
                <ComposeArea
                  label="Paste or summarise the client's reply"
                  placeholder="Client replied: asking for 3 months contract instead of full-time…"
                  onSend={() => void handleLogReply()}
                  sendLabel="Save log entry"
                />
              )}
            </>
          )}

          {/* ── SUBMITTED: approval decision ── */}
          {s === 'submitted' && (
            <>
              <div className="space-y-1.5">
                <p className="text-xs vscode-text-tertiary">Approval note (optional)</p>
                <textarea className="input w-full resize-none text-sm" rows={2}
                  placeholder="Optional note…"
                  value={approverNote} onChange={(e) => setApproverNote(e.target.value)} />
              </div>
              <ABtn variant="primary" icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                onClick={() => void handleApproveAndCreateJob()}>
                Approve &amp; create job
              </ABtn>
              <ABtn variant="secondary" icon={<Check className="w-3.5 h-3.5" />}
                onClick={() => void handleApprovalAction('approved')}>
                Approve only
              </ABtn>
              <ABtn variant="danger" icon={<XCircle className="w-3.5 h-3.5" />}
                onClick={() => void handleApprovalAction('rejected')}>
                Reject
              </ABtn>
              <ABtn variant="ghost" icon={<RotateCcw className="w-3.5 h-3.5" />}
                onClick={() => void handleSimpleTransition('draft', 'Sent back to draft for changes')}>
                Send back to draft
              </ABtn>
            </>
          )}

          {/* ── APPROVED ── */}
          {s === 'approved' && (
            <>
              {linked ? (
                <Link to={`/jobs/${linkedJobId}`}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                  style={{ background: BRAND, color: '#fff' }}>
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  Open linked job
                </Link>
              ) : (
                <button type="button"
                  onClick={() => setJobWizardOpen(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                  style={{ background: BRAND, color: '#fff' }}>
                  <Briefcase className="w-3.5 h-3.5 shrink-0" />
                  Create job from requisition
                </button>
              )}
              <ABtn variant="secondary" icon={<Play className="w-3.5 h-3.5" />}
                onClick={() => void handleSimpleTransition('sourcing', 'Moved to active sourcing')}>
                Mark sourcing (recruiting active)
              </ABtn>
            </>
          )}

          {/* ── SOURCING ── */}
          {s === 'sourcing' && (
            <>
              {linkedJobId && (
                <Link to={`/jobs/${linkedJobId}`}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                  style={{ background: `${BRAND}12`, color: BRAND, border: `1px solid ${BRAND}40` }}>
                  <Briefcase className="w-3.5 h-3.5 shrink-0" />
                  View linked job pipeline
                </Link>
              )}
              <ABtn variant="primary" icon={<CheckCircle2 className="w-3.5 h-3.5" />}
                onClick={() => void handleSimpleTransition('filled', 'Role filled — mandate complete')}>
                Mark filled
              </ABtn>
              <ABtn variant="ghost" icon={<Pause className="w-3.5 h-3.5" />}
                onClick={() => void handleSimpleTransition('on_hold', 'Client paused the search')}>
                Place on hold
              </ABtn>
            </>
          )}

          {/* ── ON HOLD ── */}
          {s === 'on_hold' && (
            <ABtn variant="primary" icon={<Play className="w-3.5 h-3.5" />}
              onClick={() => void handleSimpleTransition('sourcing', 'Resumed sourcing')}>
              Resume sourcing
            </ABtn>
          )}

          {/* ── REJECTED ── */}
          {s === 'rejected' && (
            <>
              <ABtn variant="primary" icon={<RotateCcw className="w-3.5 h-3.5" />}
                onClick={() => void handleSimpleTransition('draft', 'Reopened as draft')}>
                Reopen as draft
              </ABtn>
              <ABtn variant="secondary" icon={<Send className="w-3.5 h-3.5" />}
                onClick={() => void handleSimpleTransition('submitted', 'Resubmitted for approval')}>
                Resubmit for approval
              </ABtn>
            </>
          )}

          {/* ── CANCELLED ── */}
          {s === 'cancelled' && (
            <ABtn variant="secondary" icon={<RotateCcw className="w-3.5 h-3.5" />}
              onClick={() => void handleSimpleTransition('draft', 'Reopened from cancelled')}>
              Reopen as draft
            </ABtn>
          )}

          {/* ── FILLED ── */}
          {s === 'filled' && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: '#10b98140', background: '#10b98110', color: '#10b981' }}>
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              Mandate complete
            </div>
          )}

          {/* Cancel from active states */}
          {['draft', 'awaiting_client', 'approved', 'sourcing', 'on_hold'].includes(s) && !composeMode && (
            <button type="button"
              onClick={() => void handleSimpleTransition('cancelled', 'Cancelled')}
              disabled={actionSaving}
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded text-xs opacity-50 hover:opacity-80 transition-opacity"
              style={{ color: 'var(--vscode-text-tertiary)' }}>
              <Ban className="w-3 h-3" /> Cancel requisition
            </button>
          )}

          {/* Show recent history inline */}
          {history.length > 0 && (
            <div className="mt-1 border-t border-[var(--vscode-border-primary)] pt-2">
              <button type="button" onClick={() => setShowActionHistory((v) => !v)}
                className="flex items-center gap-1.5 text-xs vscode-text-tertiary w-full">
                {showActionHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showActionHistory ? 'Hide' : 'Show'} recent history ({history.length})
              </button>
              {showActionHistory && (
                <ol className="mt-2 space-y-1.5">
                  {history.slice(0, 5).map((e) => (
                    <li key={e.id} className="text-xs vscode-text-tertiary flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--vscode-border-primary)] shrink-0" />
                      <span className="capitalize font-medium vscode-text-secondary">{e.to_status?.replace(/_/g, ' ')}</span>
                      {e.changed_by && <span className="truncate">· {e.changed_by}</span>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      </CandidateCollapsiblePanel>
    );
  };

  const jobLinkSidebar = (prefix: string) => (canCreateJob || linkedJobId) && (
    <CandidateCollapsiblePanel storageKey={`req-${prefix}-job`} title="Linked job" defaultOpen>
      <div className="pt-3 space-y-2">
        {linkedJobId ? (
          <Link to={`/jobs/${linkedJobId}`}
            className="flex items-center gap-2 text-sm font-medium hover:underline"
            style={{ color: BRAND }}>
            <Briefcase className="w-4 h-4" />
            {req.job?.title ?? 'View linked job'} →
          </Link>
        ) : canCreateJob ? (
          <button type="button"
            onClick={() => setJobWizardOpen(true)}
            className="btn-primary text-sm w-full justify-center inline-flex items-center gap-1.5">
            <Briefcase className="w-4 h-4" /> Create job from requisition
          </button>
        ) : null}
      </div>
    </CandidateCollapsiblePanel>
  );

  /* ─── Skill chips helper ─── */
  const skillChips = (skills: string[], tinted: boolean) =>
    skills.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {skills.map((s) => (
          <span key={s} className="rounded-md px-2 py-0.5 text-xs font-medium vscode-text-primary"
            style={tinted
              ? { backgroundColor: `${BRAND}20`, border: `1px solid ${BRAND}40` }
              : { backgroundColor: 'var(--vscode-bg-hover)', border: '1px solid var(--vscode-border-primary)' }}>
            {s}
          </span>
        ))}
      </div>
    ) : <span className="text-xs vscode-text-tertiary">None specified</span>;

  /* ── Vertical heartbeat lifecycle panel ── replaces separate chart + status panels */
  const VerticalLifecyclePanel = ({ storageKey }: { storageKey: string }) => {
    const phaseIdx  = getRequisitionFlowPhaseIndex(req.status);
    const isOffPath = req.status === 'rejected' || req.status === 'cancelled' || req.status === 'on_hold';
    const currentPhase = phaseIdx >= 0 && phaseIdx < REQUISITION_FLOW_STEPS.length
      ? REQUISITION_FLOW_STEPS[phaseIdx] : null;

    return (
      <CandidateCollapsiblePanel storageKey={storageKey} title="Lifecycle &amp; status" defaultOpen>
        <div className="pt-3 space-y-4">

          {/* Off-path badge */}
          {isOffPath && (
            <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold"
              style={{ backgroundColor: req.status === 'on_hold' ? '#f59e0b20' : '#ef444420',
                       color: req.status === 'on_hold' ? '#fbbf24' : '#f87171',
                       border: `1px solid ${req.status === 'on_hold' ? '#f59e0b40' : '#ef444440'}` }}>
              {req.status === 'on_hold' ? <Pause className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
              {REQUISITION_STATUS_LABELS[req.status] ?? req.status}
            </div>
          )}

          {/* Vertical stepper */}
          <div className="relative pl-3">
            {REQUISITION_LIFECYCLE_PHASES.map((phase, idx) => {
              const done   = idx < phaseIdx && !isOffPath;
              const active = idx === phaseIdx && !isOffPath;
              const isLast = idx === REQUISITION_LIFECYCLE_PHASES.length - 1;
              const step   = REQUISITION_FLOW_STEPS.find((s) => s.key === phase.key);

              return (
                <div key={phase.key} className="relative flex gap-3">
                  {/* Vertical connector */}
                  {!isLast && (
                    <div className="absolute left-[9px] top-6 bottom-0 w-0.5"
                      style={{ backgroundColor: done ? '#10b981' : 'var(--vscode-border-primary)' }} />
                  )}

                  {/* Node */}
                  <div className="relative z-10 mt-0.5 flex-shrink-0 w-5 h-5">
                    <div className={[
                      'w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all',
                      done   ? 'border-emerald-500 bg-emerald-500' : '',
                      active ? 'animate-pulse' : '',
                      !done && !active ? 'border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)]' : '',
                    ].join(' ')}
                      style={active ? {
                        borderColor: BRAND,
                        backgroundColor: `${BRAND}25`,
                        boxShadow: `0 0 0 4px ${BRAND}25, 0 0 10px ${BRAND}40`,
                      } : {}}>
                      {done
                        ? <Check className="w-2.5 h-2.5 text-white" />
                        : active
                          ? <div className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: BRAND }} />
                          : <div className="w-1.5 h-1.5 rounded-full bg-[var(--vscode-border-primary)]" />
                      }
                    </div>
                  </div>

                  {/* Label */}
                  <div className="pb-4 min-w-0">
                    <p className={`text-sm font-semibold leading-tight ${active ? '' : done ? 'vscode-text-secondary' : 'vscode-text-tertiary'}`}
                      style={active ? { color: BRAND } : {}}>
                      {phase.short}
                    </p>
                    {active && step && (
                      <p className="text-xs vscode-text-tertiary mt-0.5 leading-relaxed">{step.description}</p>
                    )}
                    {active && (
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold"
                        style={{ backgroundColor: `${BRAND}20`, color: BRAND }}>
                        current
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reference + intake strip */}
          <div className="rounded-lg border border-[var(--vscode-border-primary)] divide-y divide-[var(--vscode-border-primary)]"
            style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
            <div className="flex justify-between px-3 py-2">
              <span className="text-xs vscode-text-tertiary">Reference</span>
              <span className="text-xs font-mono vscode-text-primary">{refLabel(req.id)}</span>
            </div>
            {req.intake_wizard_step != null && (
              <div className="flex justify-between px-3 py-2">
                <span className="text-xs vscode-text-tertiary">Intake</span>
                <span className="text-xs text-amber-400">Step {req.intake_wizard_step}/5 incomplete</span>
              </div>
            )}
            {req.client_outreach_sent_at && (
              <div className="flex justify-between px-3 py-2">
                <span className="text-xs vscode-text-tertiary">Outreach sent</span>
                <span className="text-xs vscode-text-secondary">{fmtDate(req.client_outreach_sent_at)}</span>
              </div>
            )}
            {req.submitted_at && (
              <div className="flex justify-between px-3 py-2">
                <span className="text-xs vscode-text-tertiary">Submitted</span>
                <span className="text-xs vscode-text-secondary">{fmtDate(req.submitted_at)}</span>
              </div>
            )}
            {req.approved_at && (
              <div className="flex justify-between px-3 py-2">
                <span className="text-xs vscode-text-tertiary">Approved</span>
                <span className="text-xs text-emerald-400">{fmtDate(req.approved_at)}</span>
              </div>
            )}
          </div>
        </div>
      </CandidateCollapsiblePanel>
    );
  };

  return (
    <div className="w-full min-w-0 mx-auto max-w-none">
      <RecordDetailLayout
        header={
          <RequisitionHeaderBand
            req={req}
            breadcrumb={[
              { label: 'Pipeline', href: '/requisitions' },
              { label: 'Requisitions', href: '/requisitions' },
              { label: `${refLabel(req.id)} — ${req.title}` },
            ]}
            actionsEnd={
              <ListOperationsDropdown
                objectHistoryHref={objectHistoryUrl('requisitions')}
                onExport={(fmt, scope) => listExportNotImplemented('Requisition', fmt, scope)}
              />
            }
          />
        }
        metaRail={
          <RequisitionDashboardStack
            req={req}
            orgId={user.orgId}
            onViewTimeline={() => setTimelineOpen(true)}
          />
        }
        main={
          <>
      {/* ── Tab bar — no icons, matching JobDetailPage ── */}
      <nav className="ggj-cand-tablist mb-4" aria-label="Requisition sections">
        {TABS.map(({ id: tid, label }) => (
          <button key={tid} type="button" onClick={() => setActiveTab(tid)}
            className={`ggj-cand-tab ${activeTab === tid ? 'ggj-cand-tab--active' : ''}`}>
            {label}
          </button>
        ))}
      </nav>

      {/* ══════════════════════════════════ OVERVIEW ══════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          {/* Left: single ggj-param-group card — toolbar + sections + form (mirrors JobDetailPage) */}
          <div className="ggj-param-group overflow-hidden">

            {/* Toolbar — mirrors JobDetailPage */}
            <div className="ggj-profile-toolbar">
              <div className="flex flex-wrap items-center gap-2">
                {ovEditing ? (
                  <>
                    <button type="submit" form="requisition-form" className={`${detailTbBtn} ${detailTbNeutral}`}>
                      <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                      <span>[Save]</span>
                    </button>
                    <button
                      type="button"
                      className={`${detailTbBtn} ${detailTbDanger}`}
                      onClick={() => setOvEditing(false)}
                    >
                      <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                      <span>[Discard]</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`${detailTbBtn} hover:bg-slate-200/90 dark:hover:bg-gray-700`}
                    onClick={() => setOvEditing(true)}
                  >
                    <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-primary)]" aria-hidden />
                    <span className="text-slate-700 dark:text-gray-300">Edit</span>
                  </button>
                )}
                {canCreateJob && (
                  <button type="button"
                    onClick={() => setJobWizardOpen(true)}
                    className="ggj-toolbar-btn inline-flex items-center gap-1">
                    <Briefcase className="w-3.5 h-3.5" /> Create job
                  </button>
                )}
                {linkedJobId && (
                  <Link to={`/jobs/${linkedJobId}`}
                    className="ggj-toolbar-btn inline-flex items-center gap-1">
                    <ExternalLink className="w-3.5 h-3.5" /> [Open linked job]
                  </Link>
                )}
              </div>
            </div>

            {/* Context summary header */}
            <div className="ggj-profile-section-h">
              {req.client?.name ? req.client.name.toUpperCase() : 'REQUISITION OVERVIEW'}
            </div>
            <div className="ggj-profile-section-body">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                  style={{ background: `rgba(139,128,249,0.15)`, color: BRAND, border: `1px solid rgba(139,128,249,0.3)` }}>
                  {(req.client?.name?.[0] ?? req.title[0])?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold vscode-text-primary truncate">{req.title}</p>
                  {req.client && (
                    <Link to={`/clients/${req.client_id}`}
                      className="text-sm hover:underline mt-0.5 block" style={{ color: BRAND }}>
                      {req.client.name}
                    </Link>
                  )}
                  {req.location && <p className="text-xs vscode-text-tertiary mt-0.5">{req.location}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize"
                    style={{ background: `${BRAND}20`, color: BRAND, border: `1px solid ${BRAND}40` }}>
                    {REQUISITION_STATUS_LABELS[req.status] ?? req.status}
                  </span>
                  {(() => {
                    const pc = PRIORITY_CONFIG[req.priority ?? 'normal'] ?? PRIORITY_CONFIG.normal;
                    return (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>
                        {pc.label} priority
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex flex-wrap gap-5 mt-3 pt-3 border-t vscode-border">
                <div className="text-center">
                  <p className="text-lg font-bold vscode-text-primary">{req.headcount}</p>
                  <p className="text-xs vscode-text-tertiary">Headcount</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold vscode-text-primary">{refLabel(req.id)}</p>
                  <p className="text-xs vscode-text-tertiary">Reference</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold vscode-text-primary">
                    {req.intake_wizard_step != null ? `Step ${req.intake_wizard_step}/5` : 'Complete'}
                  </p>
                  <p className="text-xs vscode-text-tertiary">Intake</p>
                </div>
                {req.target_start_date && (
                  <div className="text-center">
                    <p className="text-sm font-semibold vscode-text-primary">{fmtDate(req.target_start_date)}</p>
                    <p className="text-xs vscode-text-tertiary">Target start</p>
                  </div>
                )}
              </div>
            </div>

            {/* ── When NOT editing: structured attribute sections ── */}
            {!ovEditing && (
              <>
                {/* Role at a glance — tiles + chips, no label:value rows (C-49) */}
                <div className="ggj-profile-section-h">Role at a glance</div>
                <div className="ggj-profile-section-body">
                  <div className="flex flex-wrap items-stretch gap-2">
                    <ProgressChip
                      label="Headcount"
                      value={0}
                      target={req.headcount || 1}
                      tone="neutral"
                      testId="req-headcount-chip"
                    />
                    {(() => {
                      const m = req.title.match(/\b(senior|junior|principal|lead|staff|mid)\b/i);
                      if (!m) return null;
                      return (
                        <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize"
                          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)' }}>
                          {m[1]!.toLowerCase()}
                        </span>
                      );
                    })()}
                    {req.employment_type && (
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium capitalize"
                        style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)' }}>
                        {req.employment_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    {req.location && (
                      <span className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium"
                        style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)' }}>
                        {req.location}
                        {/\bremote\b/i.test(req.location) && (
                          <span className="ml-1 rounded px-1 text-[10px] font-semibold" style={{ background: '#10B98120', color: '#047857' }}>
                            Remote
                          </span>
                        )}
                      </span>
                    )}
                    {req.target_start_date && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium"
                        style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)' }}>
                        <span className="vscode-text-tertiary">Target start</span>
                        {fmtDate(req.target_start_date)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Compensation — currency chip + range bar (C-49) */}
                <div className="ggj-profile-section-h">Compensation</div>
                <div className="ggj-profile-section-body">
                  {req.budget_min == null && req.budget_max == null ? (
                    <p className="text-sm vscode-text-tertiary">Not specified</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)', color: 'var(--vscode-text-primary)' }}>
                          {req.budget_currency || 'USD'}
                        </span>
                        <span className="text-sm font-semibold vscode-text-primary tabular-nums">
                          {req.budget_min != null ? req.budget_min.toLocaleString() : '—'}
                          <span className="mx-1.5 vscode-text-tertiary">–</span>
                          {req.budget_max != null ? req.budget_max.toLocaleString() : '—'}
                        </span>
                      </div>
                      {req.budget_min != null && req.budget_max != null && req.budget_max > req.budget_min && (
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] vscode-text-tertiary tabular-nums">
                            {req.budget_min.toLocaleString()}
                          </span>
                          <div className="relative h-1.5 flex-1 overflow-hidden rounded-full"
                            style={{ background: 'var(--vscode-border-primary)' }}>
                            <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(90deg, #8B80F9 0%, #10B981 100%)' }} />
                          </div>
                          <span className="text-[11px] vscode-text-tertiary tabular-nums">
                            {req.budget_max.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Hiring team — AvatarStack with hover cards (C-49) */}
                <div className="ggj-profile-section-h">Hiring team</div>
                <div className="ggj-profile-section-body">
                  {(() => {
                    const people: AvatarStackPerson[] = [];
                    if (req.hiring_manager_name) {
                      people.push({
                        name: req.hiring_manager_name,
                        role: 'manager',
                        email: req.hiring_manager_email ?? null,
                      });
                    }
                    if (req.approver_user?.display_name || req.approver_user?.email) {
                      people.push({
                        name: req.approver_user.display_name ?? req.approver_user.email ?? 'Approver',
                        role: 'approver',
                        email: req.approver_user.email ?? null,
                      });
                    }
                    if (people.length === 0) {
                      return <p className="text-sm vscode-text-tertiary">No hiring team assigned yet.</p>;
                    }
                    return (
                      <div className="flex items-center gap-3">
                        <AvatarStack people={people} max={4} size={32} />
                        <p className="text-[11px] vscode-text-tertiary">
                          Hover an avatar to see contact details.
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* Required skills */}
                <div className="ggj-profile-section-h">Required skills</div>
                <div className="ggj-profile-section-body space-y-3">
                  <div>
                    <p className="text-xs vscode-text-tertiary mb-1.5">Must-have</p>
                    {skillChips(req.must_have_skills ?? [], true)}
                  </div>
                  <div>
                    <p className="text-xs vscode-text-tertiary mb-1.5">Nice-to-have</p>
                    {skillChips(req.nice_to_have_skills ?? [], false)}
                  </div>
                </div>

                {/* Business justification */}
                {req.business_justification && (
                  <>
                    <div className="ggj-profile-section-h">Business justification</div>
                    <div className="ggj-profile-section-body">
                      <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">
                        {req.business_justification}
                      </p>
                    </div>
                  </>
                )}

                {/* Internal notes */}
                {req.internal_notes && (
                  <>
                    <div className="ggj-profile-section-h">Internal notes</div>
                    <div className="ggj-profile-section-body">
                      <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">
                        {req.internal_notes}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}

            {/* ── When editing: RequisitionForm ── */}
            {ovEditing && (
              <RequisitionForm
                mode="edit"
                requisition={req}
                readOnly={false}
                orgId={user.orgId}
                userId={user.id}
                historyActor={{ displayName: user.name, orgUserId: user.orgUserId }}
                pendingAssist={pendingAssist}
                onPendingAssistConsumed={clearAssist}
                onDraftSnapshot={setAiCtx}
                onSave={(updated) => { void refreshAfterSave(updated); setOvEditing(false); }}
              />
            )}
          </div>

          {/* Right sidebar */}
          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            <VerticalLifecyclePanel storageKey={`req-${req.id}-ov-lifecycle`} />

            {/* C-40 — View full timeline */}
            <CandidateCollapsiblePanel storageKey={`req-${req.id}-ov-timeline`} title="Position timeline" defaultOpen>
              <div className="pt-2 pb-1">
                <p className="text-[11px] vscode-text-tertiary mb-2">
                  Full event stream from opened_at to now.
                </p>
                <button
                  type="button"
                  onClick={() => setTimelineOpen(true)}
                  className="btn-secondary w-full justify-center text-sm inline-flex items-center gap-1.5"
                >
                  <History className="w-3.5 h-3.5" aria-hidden />
                  View full timeline
                </button>
              </div>
            </CandidateCollapsiblePanel>

            {actionsSidebar('ov')}
            {jobLinkSidebar('ov')}

            {/* Approval decision — in sidebar when submitted */}
            {req.status === 'submitted' && (
              <CandidateCollapsiblePanel storageKey={`req-${req.id}-ov-approval`} title="Approval decision" defaultOpen>
                <div className="pt-3 space-y-3">
                  <textarea className="input w-full resize-none text-sm" rows={2}
                    placeholder="Optional approval note…"
                    value={approverNote} onChange={(e) => setApproverNote(e.target.value)} />
                  <div className="flex flex-col gap-2">
                    <button className="btn-primary text-sm justify-center" onClick={handleApproveAndCreateJob}
                      disabled={savingApproval}>
                      <CheckCircle2 className="w-4 h-4" /> [Approve &amp; create job]
                    </button>
                    <button className="btn-secondary text-sm" onClick={() => handleApprovalAction('approved')}
                      disabled={savingApproval}>
                      [Approve only]
                    </button>
                    <button className="btn-secondary text-sm text-red-400 border-red-800 hover:border-red-600"
                      onClick={() => handleApprovalAction('rejected')} disabled={savingApproval}>
                      <XCircle className="w-4 h-4" /> [Reject]
                    </button>
                  </div>
                </div>
              </CandidateCollapsiblePanel>
            )}

            {/* Job created banner */}
            {req.status === 'sourcing' && req.created_job_id && (
              <CandidateCollapsiblePanel storageKey={`req-${req.id}-ov-job-banner`} title="Linked job created" defaultOpen>
                <div className="pt-3">
                  <p className="text-xs vscode-text-tertiary mb-2">Draft job was automatically created from this requisition.</p>
                  <Link to={`/jobs/${req.created_job_id}`}
                    className="btn-secondary text-sm w-full justify-center inline-flex items-center gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" /> [Open job →]
                  </Link>
                </div>
              </CandidateCollapsiblePanel>
            )}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ DETAILS ══════════════════════════════════ */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          <div className="ggj-param-group overflow-hidden">
            {/* Toolbar */}
            <div className="ggj-profile-toolbar">
              <div className="flex flex-wrap items-center gap-2">
                {dtEditing ? (
                  <>
                    <button type="submit" form="requisition-form" className={`${detailTbBtn} ${detailTbNeutral}`}>
                      <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                      <span>[Save]</span>
                    </button>
                    <button
                      type="button"
                      className={`${detailTbBtn} ${detailTbDanger}`}
                      onClick={() => setDtEditing(false)}
                    >
                      <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                      <span>[Discard]</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`${detailTbBtn} hover:bg-slate-200/90 dark:hover:bg-gray-700`}
                    onClick={() => setDtEditing(true)}
                  >
                    <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-primary)]" aria-hidden />
                    <span className="text-slate-700 dark:text-gray-300">Edit</span>
                  </button>
                )}
              </div>
            </div>

            {/* Role details section header */}
            <div className="ggj-profile-section-h">
              {req.client?.name ? `${req.client.name.toUpperCase()} — DETAILS` : 'ROLE DETAILS'}
            </div>

            {/* View mode: extended attribute sections */}
            {!dtEditing && (
              <>
                {/* Status & approval */}
                <div className="ggj-profile-section-h">Status &amp; approval</div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={PARAM_DT}>Status</dt>
                    <dd className={PARAM_DD}>
                      <span className="inline-flex items-center gap-1.5">
                        {STATUS_ICON[req.status]}
                        {REQUISITION_STATUS_LABELS[req.status] ?? req.status}
                      </span>
                    </dd>
                    <dt className={PARAM_DT}>Approver</dt>
                    <dd className={PARAM_DD}>
                      {req.approver_user?.display_name ?? (req.approver_user_id ? '(assigned)' : '—')}
                    </dd>
                    <dt className={PARAM_DT}>Approval notes</dt>
                    <dd className={PARAM_DD}>{req.approval_notes ?? '—'}</dd>
                    <dt className={PARAM_DT}>Submitted at</dt>
                    <dd className={PARAM_DD}>{fmtDate(req.submitted_at)}</dd>
                    <dt className={PARAM_DT}>Approved at</dt>
                    <dd className={PARAM_DD}>{fmtDate(req.approved_at)}</dd>
                    <dt className={PARAM_DT}>Rejected at</dt>
                    <dd className={PARAM_DD}>{fmtDate(req.rejected_at)}</dd>
                    <dt className={PARAM_DT}>Client outreach</dt>
                    <dd className={PARAM_DD}>{fmtDate(req.client_outreach_sent_at)}</dd>
                  </dl>
                </div>

                {/* Timeline */}
                <div className="ggj-profile-section-h">Timeline</div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={PARAM_DT}>Created</dt>
                    <dd className={PARAM_DD}>{fmtDateLong(req.created_at)}</dd>
                    <dt className={PARAM_DT}>Last modified</dt>
                    <dd className={PARAM_DD}>{fmtDateLong(req.updated_at)}</dd>
                    <dt className={PARAM_DT}>Target start</dt>
                    <dd className={PARAM_DD}>{fmtDate(req.target_start_date)}</dd>
                    <dt className={PARAM_DT}>Intake step</dt>
                    <dd className={PARAM_DD}>
                      {req.intake_wizard_step != null
                        ? `Step ${req.intake_wizard_step}/5 (in progress)`
                        : 'Complete'}
                    </dd>
                  </dl>
                </div>

                {/* Linked job */}
                <div className="ggj-profile-section-h">Linked job</div>
                <div className="ggj-profile-section-body">
                  <dl className="ggj-attr-grid">
                    <dt className={PARAM_DT}>Job ID</dt>
                    <dd className={PARAM_DD}>
                      {linkedJobId
                        ? <Link to={`/jobs/${linkedJobId}`} className="hover:underline" style={{ color: BRAND }}>
                            {linkedJobId}
                          </Link>
                        : '—'}
                    </dd>
                    <dt className={PARAM_DT}>Job title</dt>
                    <dd className={PARAM_DD}>{req.job?.title ?? '—'}</dd>
                    <dt className={PARAM_DT}>Job status</dt>
                    <dd className={PARAM_DD + ' capitalize'}>{req.job?.status ?? '—'}</dd>
                  </dl>
                </div>

                {/* Internal notes */}
                <div className="ggj-profile-section-h">Internal notes</div>
                <div className="ggj-profile-section-body">
                  {req.internal_notes
                    ? <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">{req.internal_notes}</p>
                    : <p className="text-xs vscode-text-tertiary">No internal notes.</p>
                  }
                </div>
              </>
            )}

            {/* Edit mode: full RequisitionForm */}
            {dtEditing && (
              <RequisitionForm
                mode="edit"
                requisition={req}
                readOnly={false}
                orgId={user.orgId}
                userId={user.id}
                historyActor={{ displayName: user.name, orgUserId: user.orgUserId }}
                pendingAssist={pendingAssist}
                onPendingAssistConsumed={clearAssist}
                onDraftSnapshot={setAiCtx}
                onSave={(updated) => { void refreshAfterSave(updated); setDtEditing(false); }}
              />
            )}
          </div>

          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            <VerticalLifecyclePanel storageKey={`req-${req.id}-dt-lifecycle`} />
            {actionsSidebar('dt')}
            {jobLinkSidebar('dt')}
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ AI ══════════════════════════════════ */}
      {/* ══════════════════════════════════ SOURCING ══════════════════════════════════ */}
      {activeTab === 'sourcing' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          {/* ── Main column ── */}
          <div className="space-y-4">

            {/* Target candidate profile */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-profile-section-h" style={{ borderLeft: `3px solid ${BRAND}` }}>Target candidate profile</div>
              <div className="ggj-profile-section-body">
                <dl className="ggj-attr-grid">
                  <dt className={PARAM_DT}>Role title</dt>
                  <dd className={PARAM_DD + ' font-semibold'}>{req.title ?? '—'}</dd>
                  <dt className={PARAM_DT}>Department</dt>
                  <dd className={PARAM_DD}>{req.department ?? '—'}</dd>
                  <dt className={PARAM_DT}>Seniority level</dt>
                  <dd className={PARAM_DD}>
                    {req.title
                      ? (() => {
                          const t = req.title.toLowerCase();
                          if (t.includes('principal') || t.includes('staff')) return 'Staff / Principal';
                          if (t.includes('lead') || t.includes('architect')) return 'Lead / Architect';
                          if (t.includes('senior') || t.includes('sr.')) return 'Senior';
                          if (t.includes('junior') || t.includes('jr.') || t.includes('associate')) return 'Junior / Associate';
                          return 'Mid-level';
                        })()
                      : '—'}
                  </dd>
                  <dt className={PARAM_DT}>Employment type</dt>
                  <dd className={PARAM_DD}>{req.employment_type ?? '—'}</dd>
                  <dt className={PARAM_DT}>Location</dt>
                  <dd className={PARAM_DD}>{req.location ?? '—'}</dd>
                  <dt className={PARAM_DT}>Headcount</dt>
                  <dd className={PARAM_DD}>{req.headcount ?? 1}</dd>
                </dl>
              </div>
            </div>

            {/* Skills matrix */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-profile-section-h" style={{ borderLeft: `3px solid #10b981` }}>Skills matrix</div>
              <div className="ggj-profile-section-body space-y-4">
                <div>
                  <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider mb-2">Must-have skills</p>
                  {(req.must_have_skills ?? []).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(req.must_have_skills ?? []).map((s) => (
                        <span key={s} className="px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${BRAND}18`, color: BRAND, border: `1px solid ${BRAND}40` }}>{s}</span>
                      ))}
                    </div>
                  ) : <p className="text-sm vscode-text-tertiary">No must-have skills defined — edit Overview tab to add.</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider mb-2">Nice-to-have skills</p>
                  {(req.nice_to_have_skills ?? []).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {(req.nice_to_have_skills ?? []).map((s) => (
                        <span key={s} className="px-2.5 py-0.5 rounded-full text-xs font-medium vscode-text-secondary"
                          style={{ backgroundColor: 'var(--vscode-bg-hover)', border: '1px solid var(--vscode-border-primary)' }}>{s}</span>
                      ))}
                    </div>
                  ) : <p className="text-sm vscode-text-tertiary">No nice-to-have skills defined.</p>}
                </div>
              </div>
            </div>

            {/* Sourcing strategy */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-profile-section-h" style={{ borderLeft: `3px solid #f59e0b` }}>Sourcing strategy</div>
              <div className="ggj-profile-section-body">
                <dl className="ggj-attr-grid">
                  <dt className={PARAM_DT}>Primary channels</dt>
                  <dd className={PARAM_DD}>
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {['LinkedIn', 'Naukri', 'Referral', 'Direct approach'].map((ch) => (
                        <span key={ch} className="px-2 py-0.5 rounded-md text-xs font-medium"
                          style={{ backgroundColor: '#f59e0b18', color: '#f59e0b', border: '1px solid #f59e0b40' }}>{ch}</span>
                      ))}
                    </div>
                  </dd>
                  <dt className={PARAM_DT}>Target start date</dt>
                  <dd className={PARAM_DD}>{req.target_start_date ? fmtDate(req.target_start_date) : '—'}</dd>
                  <dt className={PARAM_DT}>Budget range</dt>
                  <dd className={PARAM_DD}>
                    {(req.budget_min || req.budget_max)
                      ? `${req.budget_currency ?? 'INR'} ${req.budget_min?.toLocaleString() ?? '?'} – ${req.budget_max?.toLocaleString() ?? '?'} p.a.`
                      : '—'}
                  </dd>
                  <dt className={PARAM_DT}>Priority</dt>
                  <dd className={PARAM_DD}>
                    {(() => { const pc = PRIORITY_CONFIG[req.priority ?? 'normal'] ?? PRIORITY_CONFIG.normal; return (<span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>{pc.label}</span>); })()}
                  </dd>
                  <dt className={PARAM_DT}>Linked job</dt>
                  <dd className={PARAM_DD}>
                    {req.job_id
                      ? <a href={`/jobs/${req.job_id}`} className="text-sm font-medium hover:underline" style={{ color: BRAND }}>{req.job?.title ?? 'View job'}</a>
                      : <span className="vscode-text-tertiary text-sm">Not yet linked — publish a job from Overview</span>}
                  </dd>
                </dl>
              </div>
            </div>

            {/* Interview & evaluation process */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-profile-section-h" style={{ borderLeft: `3px solid #6366f1` }}>Interview &amp; evaluation process</div>
              <div className="ggj-profile-section-body">
                <dl className="ggj-attr-grid">
                  <dt className={PARAM_DT}>Suggested rounds</dt>
                  <dd className={PARAM_DD}>
                    <ol className="space-y-1 text-sm vscode-text-secondary list-decimal list-inside">
                      <li>Recruiter screening call (30 min)</li>
                      <li>Technical / skills assessment</li>
                      <li>Hiring manager interview (60 min)</li>
                      <li>Final / stakeholder round</li>
                    </ol>
                  </dd>
                  <dt className={PARAM_DT}>Assessment type</dt>
                  <dd className={PARAM_DD}>{req.employment_type === 'Contract' ? 'Assignment / take-home task' : 'Structured competency interview'}</dd>
                  <dt className={PARAM_DT}>Notice period tolerance</dt>
                  <dd className={PARAM_DD}>Up to 60 days (standard India market)</dd>
                  <dt className={PARAM_DT}>Screening checklist</dt>
                  <dd className={PARAM_DD}>
                    <div className="space-y-1 pt-0.5">
                      {[
                        'Skills match vs must-have list',
                        'Location / remote alignment',
                        'Compensation expectation in range',
                        'Notice period acceptable',
                        'No conflict with client NDAs',
                      ].map((item) => (
                        <div key={item} className="flex items-center gap-2 text-sm vscode-text-secondary">
                          <span className="w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center"
                            style={{ borderColor: 'var(--vscode-border-primary)', backgroundColor: 'var(--vscode-bg-hover)' }}>
                          </span>
                          {item}
                        </div>
                      ))}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>

            {/* Business justification */}
            {req.business_justification && (
              <div className="ggj-param-group overflow-hidden">
                <div className="ggj-profile-section-h" style={{ borderLeft: `3px solid #ec4899` }}>Business justification</div>
                <div className="ggj-profile-section-body">
                  <p className="text-sm vscode-text-secondary leading-relaxed whitespace-pre-wrap">{req.business_justification}</p>
                </div>
              </div>
            )}
          </div>

          {/* ── Right sidebar ── */}
          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            <CandidateCollapsiblePanel storageKey={`req-${req.id}-src-metrics`} title="Pipeline metrics" defaultOpen>
              <div className="pt-3 space-y-3">
                {([
                  { k: 'Headcount target', v: String(req.headcount ?? 1), c: BRAND },
                  { k: 'Positions open',   v: String(req.headcount ?? 1), c: '#10b981' },
                  { k: 'Linked job',       v: req.job_id ? 'Yes' : 'Not yet', c: req.job_id ? '#10b981' : '#f59e0b' },
                  { k: 'Status',           v: REQUISITION_STATUS_LABELS[req.status] ?? req.status, c: BRAND },
                ]).map(({ k, v, c }) => (
                  <div key={k} className="flex items-center justify-between rounded-lg px-3 py-2 border"
                    style={{ borderColor: `${c}30`, backgroundColor: `${c}08` }}>
                    <span className="text-xs vscode-text-secondary">{k}</span>
                    <span className="text-xs font-semibold" style={{ color: c }}>{v}</span>
                  </div>
                ))}
              </div>
            </CandidateCollapsiblePanel>

            <CandidateCollapsiblePanel storageKey={`req-${req.id}-src-contacts`} title="Hiring contacts" defaultOpen>
              <div className="pt-3 space-y-3">
                {req.hiring_manager_name ? (
                  <div className="flex items-center gap-2 rounded-lg border px-3 py-2"
                    style={{ borderColor: '#10b98140', backgroundColor: '#10b98108' }}>
                    <span className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: '#10b981' }}>
                      {req.hiring_manager_name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold vscode-text-primary truncate">{req.hiring_manager_name}</p>
                      <p className="text-[10px] vscode-text-tertiary truncate">{req.hiring_manager_email ?? 'No email'}</p>
                      <p className="text-[10px] font-medium" style={{ color: '#10b981' }}>Primary hiring manager</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs vscode-text-tertiary">No hiring manager set — add from Overview tab.</p>
                )}
              </div>
            </CandidateCollapsiblePanel>

            <VerticalLifecyclePanel storageKey={`req-${req.id}-src-lifecycle`} />
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ HISTORY ══════════════════════════════════ */}
      {activeTab === 'history' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">
          <div className="ggj-param-group overflow-hidden">
            <div className="ggj-profile-toolbar">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold vscode-text-primary uppercase tracking-wider">
                  Status history
                </span>
                <span className="text-xs vscode-text-tertiary font-mono">({history.length} events)</span>
              </div>
            </div>
            {history.length === 0 ? (
              <div className="ggj-profile-section-body">
                <p className="text-sm vscode-text-tertiary text-center py-6">No status changes recorded yet.</p>
              </div>
            ) : (
              <div className="ggj-profile-section-body">
                <ol className="relative border-l border-[var(--vscode-border-primary)] space-y-4 pl-6">
                  {history.map((entry) => (
                    <li key={entry.id} className="relative">
                      <span className="absolute -left-[1.1rem] top-0.5">
                        {STATUS_ICON[entry.to_status] ?? <Clock className="w-4 h-4 vscode-text-tertiary" />}
                      </span>
                      <div className="rounded-lg border border-[var(--vscode-border-primary)] p-4 bg-[var(--vscode-bg-elevated)]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium vscode-text-primary capitalize">
                            {REQUISITION_STATUS_LABELS[entry.to_status as RequisitionStatus] ?? entry.to_status}
                          </span>
                          {entry.from_status && (
                            <span className="text-xs vscode-text-tertiary">
                              ← {REQUISITION_STATUS_LABELS[entry.from_status as RequisitionStatus] ?? entry.from_status}
                            </span>
                          )}
                        </div>
                        {entry.note && <p className="text-sm vscode-text-secondary mb-1">{entry.note}</p>}
                        <p className="text-xs vscode-text-tertiary">
                          {entry.changed_by ?? 'System'} · {fmtDateLong(entry.changed_at)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <aside className="ggj-rail-aside flex flex-col gap-3 lg:sticky lg:top-4">
            <VerticalLifecyclePanel storageKey={`req-${req.id}-hist-lifecycle`} />
            <CandidateCollapsiblePanel storageKey={`req-${req.id}-hist-summary`} title="Timeline summary" defaultOpen>
              <div className="pt-3 space-y-2">
                {([
                  { k: 'Status changes', v: history.length },
                  { k: 'Created',        v: fmtDate(req.created_at) },
                  req.submitted_at ? { k: 'Submitted', v: fmtDate(req.submitted_at) } : null,
                  req.approved_at  ? { k: 'Approved',  v: fmtDate(req.approved_at)  } : null,
                  req.rejected_at  ? { k: 'Rejected',  v: fmtDate(req.rejected_at)  } : null,
                ] as ({ k: string; v: string | number } | null)[])
                  .filter(Boolean)
                  .map((row) => {
                    const r = row as { k: string; v: string | number };
                    return (
                      <div key={r.k} className="flex justify-between">
                        <span className="vscode-text-tertiary text-xs">{r.k}</span>
                        <span className="vscode-text-primary text-xs font-medium">{r.v}</span>
                      </div>
                    );
                  })}
              </div>
            </CandidateCollapsiblePanel>
          </aside>
        </div>
      )}

      {/* ══════════════════════════════════ PARAMETERS ══════════════════════════════════ */}
      {activeTab === 'parameters' && (
        <div className="ggj-param-group overflow-hidden">
          <div className="ggj-profile-toolbar">
            <span className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">Parameters</span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={`${detailTbBtn} hover:bg-slate-200/90 dark:hover:bg-gray-700 inline-flex items-center gap-1.5`}
                onClick={() => {
                  setActiveTab('overview');
                  setOvEditing(true);
                }}
              >
                <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-primary)]" aria-hidden />
                <span className="text-slate-700 dark:text-gray-300">Edit</span>
              </button>
            </div>
          </div>

          {/* Object — name, description, type (matches candidate Parameters) */}
          <div className="ggj-param-section-block border-b border-[color-mix(in_srgb,var(--vscode-border-primary)_55%,transparent)]">
            <div className="ggj-profile-section-h">Object</div>
            <div className="ggj-profile-section-body">
              <dl className="ggj-attr-grid">
                <dt className={PARAM_DT}>Name</dt>
                <dd className={`${PARAM_DD} font-medium`}>{req.title}</dd>
                <dt className={PARAM_DT}>Object type</dt>
                <dd className={`${PARAM_DD} vscode-text-secondary`}>Requisition (read-only)</dd>
                <dt className={PARAM_DT}>Description</dt>
                <dd className={`${PARAM_DD} min-w-0 lg:col-span-3`}>
                  <textarea
                    readOnly
                    tabIndex={-1}
                    rows={5}
                    value={[req.business_justification, req.internal_notes].filter(Boolean).join('\n\n').trim()}
                    placeholder="—"
                    aria-label="Description"
                    className="ggj-param-readonly-textarea"
                  />
                </dd>
              </dl>
            </div>
          </div>

          {/* Role & client */}
          <div className="ggj-profile-section-h">Role &amp; client</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Client</dt>
              <dd className={PARAM_DD}>
                {req.client
                  ? <Link to={`/clients/${req.client_id}`} className="hover:underline" style={{ color: BRAND }}>{req.client.name}</Link>
                  : '—'}
              </dd>
              <dt className={PARAM_DT}>Department</dt>
              <dd className={PARAM_DD}>{req.department ?? '—'}</dd>
              <dt className={PARAM_DT}>Headcount</dt>
              <dd className={PARAM_DD}>{req.headcount}</dd>
              <dt className={PARAM_DT}>Priority</dt>
              <dd className={PARAM_DD}>
                {(() => { const pc = PRIORITY_CONFIG[req.priority ?? 'normal'] ?? PRIORITY_CONFIG.normal; return (<span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: pc.bg, color: pc.color, border: `1px solid ${pc.border}` }}>{pc.label}</span>); })()}
              </dd>
              <dt className={PARAM_DT}>Location</dt>
              <dd className={PARAM_DD}>{req.location ?? '—'}</dd>
              <dt className={PARAM_DT}>Employment type</dt>
              <dd className={PARAM_DD}>{req.employment_type ?? '—'}</dd>
              <dt className={PARAM_DT}>Target start</dt>
              <dd className={PARAM_DD}>{fmtDate(req.target_start_date)}</dd>
            </dl>
          </div>

          {/* Status & timeline */}
          <div className="ggj-profile-section-h">Status &amp; timeline</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Status</dt>
              <dd className={PARAM_DD}>
                <span className="inline-flex items-center gap-1.5">
                  {STATUS_ICON[req.status]}
                  {REQUISITION_STATUS_LABELS[req.status] ?? req.status}
                </span>
              </dd>
              <dt className={PARAM_DT}>Created</dt>
              <dd className={PARAM_DD}>{fmtDateLong(req.created_at)}</dd>
              <dt className={PARAM_DT}>Last modified</dt>
              <dd className={PARAM_DD}>{fmtDateLong(req.updated_at)}</dd>
              <dt className={PARAM_DT}>Target start</dt>
              <dd className={PARAM_DD}>{fmtDate(req.target_start_date)}</dd>
              <dt className={PARAM_DT}>Client outreach</dt>
              <dd className={PARAM_DD}>{fmtDate(req.client_outreach_sent_at)}</dd>
              <dt className={PARAM_DT}>Submitted</dt>
              <dd className={PARAM_DD}>{fmtDate(req.submitted_at)}</dd>
              <dt className={PARAM_DT}>Approved</dt>
              <dd className={PARAM_DD}>{fmtDate(req.approved_at)}</dd>
              <dt className={PARAM_DT}>Rejected</dt>
              <dd className={PARAM_DD}>{fmtDate(req.rejected_at)}</dd>
              <dt className={PARAM_DT}>Intake step</dt>
              <dd className={PARAM_DD}>
                {req.intake_wizard_step != null
                  ? `Step ${req.intake_wizard_step}/5 (in progress)`
                  : 'Complete'}
              </dd>
            </dl>
          </div>

          {/* Hiring manager */}
          <div className="ggj-profile-section-h">Hiring manager</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Name</dt>
              <dd className={PARAM_DD}>{req.hiring_manager_name ?? '—'}</dd>
              <dt className={PARAM_DT}>Email</dt>
              <dd className={PARAM_DD}>
                {req.hiring_manager_email
                  ? <a href={`mailto:${req.hiring_manager_email}`} className="hover:underline" style={{ color: BRAND }}>
                      {req.hiring_manager_email}
                    </a>
                  : '—'}
              </dd>
            </dl>
          </div>

          {/* Approval */}
          <div className="ggj-profile-section-h">Approval</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Approver</dt>
              <dd className={PARAM_DD}>
                {req.approver_user?.display_name
                  ?? (req.approver_user_id ? `ID: ${req.approver_user_id}` : '—')}
              </dd>
              <dt className={PARAM_DT}>Approver email</dt>
              <dd className={PARAM_DD}>{req.approver_user?.email ?? '—'}</dd>
              <dt className={PARAM_DT}>Approval notes</dt>
              <dd className={PARAM_DD}>{req.approval_notes ?? '—'}</dd>
            </dl>
          </div>

          {/* Budget */}
          <div className="ggj-profile-section-h">Budget</div>
          <div className="ggj-profile-section-body">
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Currency</dt>
              <dd className={PARAM_DD}>{req.budget_currency || '—'}</dd>
              <dt className={PARAM_DT}>Minimum</dt>
              <dd className={PARAM_DD}>
                {req.budget_min != null
                  ? `${req.budget_currency} ${req.budget_min.toLocaleString()}`
                  : '—'}
              </dd>
              <dt className={PARAM_DT}>Maximum</dt>
              <dd className={PARAM_DD}>
                {req.budget_max != null
                  ? `${req.budget_currency} ${req.budget_max.toLocaleString()}`
                  : '—'}
              </dd>
            </dl>
          </div>

          {/* Required skills */}
          <div className="ggj-profile-section-h">Required skills</div>
          <div className="ggj-profile-section-body space-y-3">
            <div>
              <p className="text-xs vscode-text-tertiary mb-1.5">Must-have</p>
              {skillChips(req.must_have_skills ?? [], true)}
            </div>
            <div>
              <p className="text-xs vscode-text-tertiary mb-1.5">Nice-to-have</p>
              {skillChips(req.nice_to_have_skills ?? [], false)}
            </div>
          </div>

          {/* Narrative text lives under Object → Description (business justification + internal notes). */}

          {/* System attributes */}
          <div className="ggj-profile-section-h">System attributes</div>
          <div className="ggj-profile-section-body">
            <p className="text-xs vscode-text-tertiary mb-2">Immutable audit fields — cannot be edited.</p>
            <dl className="ggj-attr-grid">
              <dt className={PARAM_DT}>Requisition ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={req.id}>{req.id}</dd>
              <dt className={PARAM_DT}>Reference</dt>
              <dd className={PARAM_DD + ' font-mono text-xs'}>{refLabel(req.id)}</dd>
              <dt className={PARAM_DT}>Organisation ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={req.org_id}>{req.org_id}</dd>
              <dt className={PARAM_DT}>Client ID</dt>
              <dd className={PARAM_DD + ' font-mono text-xs truncate'} title={req.client_id}>{req.client_id}</dd>
              <dt className={PARAM_DT}>Created by</dt>
              <dd className={PARAM_DD + ' text-xs'}>{req.created_by}</dd>
              <dt className={PARAM_DT}>Created when</dt>
              <dd className={PARAM_DD + ' text-xs'}>{fmtDateLong(req.created_at)}</dd>
              <dt className={PARAM_DT}>Modified when</dt>
              <dd className={PARAM_DD + ' text-xs'}>{fmtDateLong(req.updated_at)}</dd>
              <dt className={PARAM_DT}>Linked job</dt>
              <dd className={PARAM_DD + ' font-mono text-xs'}>
                {linkedJobId
                  ? <Link to={`/jobs/${linkedJobId}`} className="hover:underline" style={{ color: BRAND }}>{linkedJobId}</Link>
                  : '—'}
              </dd>
            </dl>
          </div>
        </div>
      )}

          </>
        }
      />

      {/* ── Email compose modal ── */}
      <Modal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        title={emailIsResend ? 'Resend outreach email' : 'Email hiring client'}
        subtitle={`Sending to: ${composeToEmail || 'no email set'}`}
        icon={<Mail className="w-4 h-4" />}
        size="2xl"
      >
        {/* Two-column layout: compose form left | templates & AI right */}
        <div className="flex gap-5">

          {/* Left: compose form */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* To */}
            <div>
              <label className="label text-xs vscode-text-secondary">To (recipient email)</label>
              <input className="input w-full" type="email" value={composeToEmail}
                onChange={(e) => setComposeToEmail(e.target.value)} placeholder="hiring.manager@client.com" />
              {!composeToEmail.trim() && (
                <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Add a hiring manager email on the Overview tab first, or type it here.
                </p>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="label text-xs vscode-text-secondary">Subject</label>
              <input className="input w-full" value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)} />
            </div>

            {/* Body */}
            <div>
              <label className="label text-xs vscode-text-secondary">Message body</label>
              <textarea className="input w-full resize-y text-sm" rows={11} value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)} placeholder="Type your message here…" />
            </div>

            {/* Req summary */}
            <div className="rounded-lg border border-[var(--vscode-border-primary)] p-3"
              style={{ backgroundColor: `${BRAND}08` }}>
              <p className="text-xs font-semibold vscode-text-tertiary uppercase tracking-wider mb-2">Requisition context</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                <span className="vscode-text-tertiary">Role</span><span className="vscode-text-primary font-medium">{req.title}</span>
                <span className="vscode-text-tertiary">Client</span><span className="vscode-text-primary">{req.client?.name ?? '—'}</span>
                <span className="vscode-text-tertiary">Headcount</span><span className="vscode-text-primary">{req.headcount}</span>
                {req.location && <><span className="vscode-text-tertiary">Location</span><span className="vscode-text-primary">{req.location}</span></>}
              </div>
            </div>

            {actionError && (
              <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/30 rounded-lg px-3 py-2">{actionError}</p>
            )}

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--vscode-border-primary)]">
              <button type="button" className="btn-secondary text-sm" onClick={() => setEmailModalOpen(false)}>Cancel</button>
              <button type="button" disabled={actionSaving || !composeToEmail.trim()}
                className="btn-primary text-sm inline-flex items-center gap-2" style={{ background: BRAND }}
                onClick={() => void handleSendEmail(emailIsResend)}>
                {actionSaving ? <Clock className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {emailIsResend ? 'Resend email' : 'Send email'}
              </button>
            </div>
          </div>

          {/* Right: templates + AI panel */}
          <div className="w-[210px] flex-shrink-0 border-l border-[var(--vscode-border-primary)] pl-4 space-y-5 overflow-y-auto max-h-[520px]">

            {/* Email templates */}
            <div className="space-y-2">
              <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">Templates</p>
              {[
                {
                  label: 'Outreach',
                  body: `Dear ${req.hiring_manager_name ?? 'Hiring Manager'},\n\nWe have received your hiring requisition for the role of ${req.title}${req.client?.name ? ` at ${req.client.name}` : ''}.\n\nWe are reviewing the requirements and will be in touch with a shortlist within the agreed timeline. Kindly confirm this mandate so we can begin sourcing.\n\nPlease reply with any additional preferences or changes.\n\nBest regards,\n[Your name]`,
                },
                {
                  label: 'Follow-up',
                  body: `Dear ${req.hiring_manager_name ?? 'Hiring Manager'},\n\nFollowing up on our earlier communication regarding the ${req.title} requisition.\n\nWe haven't received a confirmation yet. Could you please confirm the mandate at your earliest convenience so we can start sourcing candidates?\n\nBest regards,\n[Your name]`,
                },
                {
                  label: 'Confirmation request',
                  body: `Dear ${req.hiring_manager_name ?? 'Hiring Manager'},\n\nWe are ready to proceed with sourcing for the ${req.title} position. Before we begin, we need your written confirmation on:\n\n• Approved headcount: ${req.headcount}\n${req.budget_min || req.budget_max ? `• Budget range: ${req.budget_currency} ${req.budget_min ?? '?'} – ${req.budget_max ?? '?'}\n` : ''}${req.location ? `• Location: ${req.location}\n` : ''}• Target start date: ${req.target_start_date ?? 'TBD'}\n\nKindly reply to confirm.\n\nBest regards,\n[Your name]`,
                },
              ].map((tpl) => (
                <button key={tpl.label} type="button"
                  className="w-full text-left rounded-lg border border-[var(--vscode-border-primary)] px-2.5 py-2 hover:bg-[var(--vscode-bg-hover)] transition-colors group"
                  onClick={() => { setComposeBody(tpl.body); }}>
                  <p className="text-xs font-medium vscode-text-secondary group-hover:text-[var(--vscode-text-primary)]">{tpl.label}</p>
                  <p className="text-[10px] vscode-text-tertiary mt-0.5 line-clamp-2 leading-relaxed">
                    {tpl.body.split('\n')[0]}
                  </p>
                </button>
              ))}
            </div>

            {/* AI improve */}
            <div className="space-y-2">
              <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND }} /> AI improve
              </p>
              {[
                { label: 'Make formal',      prompt: 'Rewrite this email in a more formal, professional tone. Keep all key information.' },
                { label: 'Make concise',     prompt: 'Shorten this email to 3–4 sentences while preserving the main ask.' },
                { label: 'Add call-to-action', prompt: 'Add a clear call-to-action at the end asking the hiring manager to reply with confirmation.' },
                { label: 'Improve clarity',  prompt: 'Improve the clarity and structure of this email without changing the intent.' },
              ].map((item) => (
                <button key={item.label} type="button"
                  disabled={emailAiLoading || !composeBody.trim()}
                  className="w-full text-left text-xs rounded-lg px-2.5 py-1.5 font-medium border transition-colors hover:opacity-90"
                  style={{ borderColor: `${BRAND}40`, backgroundColor: `${BRAND}10`, color: BRAND }}
                  onClick={async () => {
                    if (!req || !user?.orgId) return;
                    setEmailAiLoading(true); setEmailAiOutput('');
                    try {
                      const { content } = await invokeLlamaChat(
                        [
                          { role: 'system', content: 'You are an expert professional email writer. Output only the rewritten email body, no explanation.' },
                          { role: 'user', content: `${item.prompt}\n\nOriginal email:\n${composeBody}` },
                        ],
                        undefined,
                        { orgId: user.orgId, feature: 'email_ai', orgUserId: user.orgUserId ?? null }
                      );
                      setEmailAiOutput(content);
                    } catch { setEmailAiOutput(''); }
                    finally { setEmailAiLoading(false); }
                  }}>
                  {emailAiLoading ? <SpinIcon className="w-3 h-3 animate-spin inline mr-1" /> : <Wand2 className="w-3 h-3 inline mr-1" />}
                  {item.label}
                </button>
              ))}

              {/* AI output preview + apply */}
              {emailAiOutput && (
                <div className="rounded-lg border border-[var(--vscode-border-primary)] p-2.5 space-y-2"
                  style={{ backgroundColor: 'var(--vscode-bg-elevated)' }}>
                  <p className="text-[10px] font-semibold vscode-text-tertiary uppercase tracking-wider">Preview</p>
                  <p className="text-xs vscode-text-secondary whitespace-pre-wrap leading-relaxed max-h-32 overflow-y-auto">{emailAiOutput}</p>
                  <button type="button"
                    className="w-full text-xs py-1 rounded font-semibold"
                    style={{ backgroundColor: BRAND, color: '#fff' }}
                    onClick={() => { setComposeBody(emailAiOutput); setEmailAiOutput(''); }}>
                    Apply to email
                  </button>
                </div>
              )}
            </div>

          </div>{/* end right panel */}
        </div>{/* end two-column */}
      </Modal>

      {/* C-40 — Position timeline drawer */}
      <PositionTimelineDrawer
        positionId={timelineOpen && req ? req.id : null}
        onClose={() => setTimelineOpen(false)}
      />

      {/* Intake wizard modal — opens on top of this page; Close returns here */}
      {intakeOpen && req && (
        <RequisitionIntakeWizardPage
          modalReqId={req.id}
          modalSeed={req}
          onModalClose={() => {
            setIntakeOpen(false);
            // Reload the requisition so intake_wizard_step updates
            requisitionService.getById(req.id).then((fresh) => {
              if (fresh) setReq(fresh);
            });
          }}
        />
      )}

      {/* Job creation wizard modal — prefilled from this requisition */}
      {jobWizardOpen && req && user?.orgId && user?.orgUserId && (
        <JobNewWizardModal
          open={jobWizardOpen}
          onClose={() => setJobWizardOpen(false)}
          onCreated={(job) => {
            setJobWizardOpen(false);
            // Refresh the requisition so the linked job appears
            requisitionService.getById(req.id).then((fresh) => {
              if (fresh) setReq(fresh);
            });
            navigate(`/jobs/${job.id}`);
          }}
          orgId={user.orgId}
          userId={user.orgUserId}
          initialClientId={req.client_id ?? undefined}
          prefillRequisition={req}
          requisitionId={req.id}
        />
      )}
    </div>
  );
}
